import React, { useEffect, useState, useRef } from 'react';
import { AssetFooter } from './AssetFooter';
import { VisualAssetData } from './types';
import { formatVersionLabel } from '@/utils/dateUtils';

interface AssetViewerProps {
  data: VisualAssetData;
  className?: string;
  showHeader?: boolean;
  showFooter?: boolean;
  width?: string | number;
  height?: string | number;
  marginPct?: number;
  fontScale?: 'extra-small' | 'small' | 'normal' | 'large';
  fillContainer?: boolean;
}

export const AssetViewer: React.FC<AssetViewerProps> = ({
  data,
  className = '',
  showHeader = true,
  showFooter = true,
  width = 800,
  height = 600,
  marginPct = 0.025,
  fontScale = 'normal',
  fillContainer = false
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [assetSize, setAssetSize] = useState({ width: 0, height: 0 });
  const [containerWidth, setContainerWidth] = useState(0);

  // Get font classes based on scale and container width
  const getFontClasses = () => {
    // Dynamic font scaling based on container width
    const effectiveScale = containerWidth > 0 && containerWidth <= 600 ? 'extra-small' : fontScale;

    switch (effectiveScale) {
      case 'extra-small':
        return {
          header: 'text-xs font-bold',
          subheader: 'text-[10px]'
        };
      case 'small':
        return {
          header: 'text-sm font-bold',
          subheader: 'text-xs'
        };
      case 'large':
        return {
          header: 'text-xl font-bold',
          subheader: 'text-base'
        };
      default: // normal
        return {
          header: 'text-lg font-bold',
          subheader: 'text-sm'
        };
    }
  };

  const fontClasses = getFontClasses();

  const {
    aspectRatio,
    viewMode,
    header,
    subheader,
    companyName,
    companyLogo,
    primaryImage,
    secondaryImage,
    primaryImages,
    secondaryImages,
    selectedPrimaryVersion,
    selectedSecondaryVersion
  } = data;

  // Helper function to generate Cloudinary URL with viewport transformations
  const generateCloudinaryUrl = (originalUrl: string, viewportState: any, aspectRatio: string) => {
    if (!viewportState || !originalUrl) return originalUrl;

    const { pan_x = 0, pan_y = 0, zoom = 1 } = viewportState;

    // Calculate crop parameters based on viewport state
    // This mirrors the editor's viewport-to-cloudinary transformation logic
    const viewportWidth = 800; // Standard viewport width
    const viewportHeight = 600; // Standard viewport height

    // Apply zoom scaling
    const cropWidth = Math.round(viewportWidth / zoom);
    const cropHeight = Math.round(viewportHeight / zoom);

    // Apply pan offset (convert from center-based to top-left based coordinates)
    const cropX = Math.max(0, Math.round(-pan_x / zoom + (viewportWidth - cropWidth) / 2));
    const cropY = Math.max(0, Math.round(-pan_y / zoom + (viewportHeight - cropHeight) / 2));

    // Build Cloudinary transformation URL
    const baseUrl = originalUrl.split('/upload/')[0] + '/upload/';
    const imagePath = originalUrl.split('/upload/')[1];

    const cropTransform = `c_crop,x_${cropX},y_${cropY},w_${cropWidth},h_${cropHeight}`;
    const scaleTransform = `c_scale,w_${viewportWidth},h_${viewportHeight}`;

    return `${baseUrl}${cropTransform}/${scaleTransform}/${imagePath}`;
  };

  // Determine the actual image URLs to display
  // If we have cropped URLs from the editor/database, use them directly
  // Otherwise fall back to viewport transformations for backward compatibility
  const baseImageUrl = primaryImages?.find(img => img.version === selectedPrimaryVersion)?.url || primaryImage;
  const primaryImageUrl = baseImageUrl; // Use the URL directly (already cropped if from database)

  const baseSecondaryUrl = secondaryImages?.find(img => img.version === selectedSecondaryVersion)?.url || secondaryImage;
  const secondaryImageUrl = baseSecondaryUrl; // Use the URL directly (already cropped if from database)

  // Get aspect ratio values
  const getAspectRatioValues = () => {
    switch (aspectRatio) {
      case '1:1':
        return { width: 1, height: 1 };
      case '16:9':
        return { width: 16, height: 9 };
      case '4:3':
        return { width: 4, height: 3 };
      default:
        return { width: 16, height: 9 };
    }
  };

  const aspectValues = getAspectRatioValues();

  // Convert aspect ratio string to number (matching editor logic)
  const getAspectRatioValue = () => {
    switch (aspectRatio) {
      case '1:1': return 1;
      case '16:9': return 16/9;
      case '4:3': return 4/3;
      default: return 1;
    }
  };

  // Responsive sizing calculation (matching editor logic)
  const resizeAsset = () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const W = canvas.clientWidth;
    const H = canvas.clientHeight;
    const MARGIN_PCT = marginPct; // Configurable spacing
    const aspect = getAspectRatioValue();

    // Track container width for responsive font sizing
    setContainerWidth(W);

    let targetW: number, targetH: number;

    // If fillContainer is true, ignore aspect ratio and fill the entire container
    if (fillContainer) {
      targetW = W * (1 - 2 * MARGIN_PCT);
      targetH = H * (1 - 2 * MARGIN_PCT);
    } else if (aspect === 1) {
      // Square: 5% on ALL sides -> use 90% of the limiting dimension
      const size = Math.min(W, H) * (1 - 2 * MARGIN_PCT);
      targetW = size;
      targetH = size;
    } else {
      // Non-square: 5% spacing on exactly TWO sides (letterboxing)
      // Option A: 5% left/right (width-bound), height flexes
      const widthBoundW = W * (1 - 2 * MARGIN_PCT);
      const widthBoundH = widthBoundW / aspect;
      const widthBoundFits = widthBoundH <= H;

      // Option B: 5% top/bottom (height-bound), width flexes
      const heightBoundH = H * (1 - 2 * MARGIN_PCT);
      const heightBoundW = heightBoundH * aspect;
      const heightBoundFits = heightBoundW <= W;

      // Prefer the option that fits and yields larger area
      const areaA = widthBoundFits ? widthBoundW * widthBoundH : -1;
      const areaB = heightBoundFits ? heightBoundW * heightBoundH : -1;

      if (areaA >= areaB && areaA > 0) {
        targetW = widthBoundW;
        targetH = widthBoundH;
      } else if (areaB > 0) {
        targetW = heightBoundW;
        targetH = heightBoundH;
      } else {
        // Edge case fallback
        if (widthBoundH <= H) {
          targetW = widthBoundW;
          targetH = widthBoundH;
        } else {
          targetW = heightBoundW;
          targetH = heightBoundH;
        }
      }
    }

    setAssetSize({ width: targetW, height: targetH });
  };

  // Resize effect
  useEffect(() => {
    resizeAsset();

    const handleResize = () => resizeAsset();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [aspectRatio, viewMode]); // Remove containerWidth from dependencies to prevent infinite loop

  // Clean asset card content layout system matching the editor's grid structure
  const renderCardContent = () => {
    const imageStyle = {
      width: '100%',
      height: '100%',
      objectFit: 'contain' as const
    };

    if (viewMode === 'single') {
      return (
        <div
          className="border-b border-gray-300 flex flex-col h-full"
        >
          {/* Image - takes all available space */}
          <div className="bg-white flex items-center justify-center p-2 flex-1 min-h-0">
            <div className="relative overflow-hidden rounded min-h-[80px] border border-gray-400 w-full h-full">
              {primaryImageUrl && (
                <img
                  src={primaryImageUrl}
                  alt="Primary asset"
                  className="absolute top-0 left-0 pointer-events-none"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              )}
            </div>
          </div>
          {/* Version - fixed height */}
          <div className="bg-white flex items-center justify-center text-xs font-medium px-2 h-10 flex-shrink-0">
            {selectedPrimaryVersion && (
              <div className="px-2 py-1 bg-muted rounded-md text-xs text-muted-foreground">
                {formatVersionLabel(selectedPrimaryVersion)}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (viewMode === 'side_by_side') {
      return (
        <div className="border-b border-gray-300 flex flex-col h-full">
          {/* Images row - takes all available space */}
          <div className="flex flex-1 min-h-0">
            {/* Left Image */}
            <div className="bg-white flex items-center justify-center p-2 flex-1 border-r border-gray-300">
              <div className="relative overflow-hidden rounded min-h-[80px] border border-gray-400 w-full h-full">
                {primaryImageUrl && (
                  <img
                    src={primaryImageUrl}
                    alt="Primary asset"
                    className="absolute top-0 left-0 pointer-events-none"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                )}
              </div>
            </div>
            {/* Right Image */}
            <div className="bg-white flex items-center justify-center p-2 flex-1">
              <div className="relative overflow-hidden rounded min-h-[80px] border border-gray-400 w-full h-full">
                {secondaryImageUrl && (
                  <img
                    src={secondaryImageUrl}
                    alt="Secondary asset"
                    className="absolute top-0 left-0 pointer-events-none"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          {/* Versions row - fixed height */}
          <div className="flex h-10 flex-shrink-0">
            {/* Left Version */}
            <div className="bg-white flex items-center justify-center text-xs font-medium px-2 flex-1 border-r border-gray-300">
              {selectedPrimaryVersion && (
                <div className="px-2 py-1 bg-muted rounded-md text-xs text-muted-foreground">
                  {formatVersionLabel(selectedPrimaryVersion)}
                </div>
              )}
            </div>
            {/* Right Version */}
            <div className="bg-white flex items-center justify-center text-xs font-medium px-2 flex-1">
              {selectedSecondaryVersion && (
                <div className="px-2 py-1 bg-muted rounded-md text-xs text-muted-foreground">
                  {formatVersionLabel(selectedSecondaryVersion)}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (viewMode === 'stacked') {
      return (
        <div className="border-b border-gray-300 flex flex-col h-full">
          {/* Top Image - takes half available space */}
          <div className="bg-white flex items-center justify-center p-2 flex-1 min-h-0">
            <div className="relative overflow-hidden rounded min-h-[80px] border border-gray-400 w-full h-full">
              {primaryImageUrl && (
                <img
                  src={primaryImageUrl}
                  alt="Primary asset"
                  className="absolute top-0 left-0 pointer-events-none"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              )}
            </div>
          </div>
          {/* Top Version - fixed height */}
          <div className="bg-white flex items-center justify-center text-xs font-medium px-2 h-10 flex-shrink-0">
            {selectedPrimaryVersion && (
              <div className="px-2 py-1 bg-muted rounded-md text-xs text-muted-foreground">
                {formatVersionLabel(selectedPrimaryVersion)}
              </div>
            )}
          </div>
          {/* Bottom Image - takes half available space */}
          <div className="bg-white flex items-center justify-center p-2 flex-1 min-h-0">
            <div className="relative overflow-hidden rounded min-h-[80px] border border-gray-400 w-full h-full">
              {secondaryImageUrl && (
                <img
                  src={secondaryImageUrl}
                  alt="Secondary asset"
                  className="absolute top-0 left-0 pointer-events-none"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              )}
            </div>
          </div>
          {/* Bottom Version - fixed height */}
          <div className="bg-white flex items-center justify-center text-xs font-medium px-2 h-10 flex-shrink-0">
            {selectedSecondaryVersion && (
              <div className="px-2 py-1 bg-muted rounded-md text-xs text-muted-foreground">
                {formatVersionLabel(selectedSecondaryVersion)}
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  // Determine if we should actually render the header (has content)
  const hasHeaderContent = showHeader && (header || subheader);

  // When fillContainer is true, skip the responsive sizing and just fill the parent
  if (fillContainer) {
    return (
      <div
        ref={canvasRef}
        className={`w-full h-full ${className}`}
      >
        <div
          className="w-full h-full bg-white shadow-xl rounded-xl border-2 border-gray-300 overflow-hidden flex flex-col"
        >
          {/* Header Section - Conditionally rendered */}
          {hasHeaderContent && (
            <div className="bg-white flex flex-col items-center justify-center px-4 py-2 border-b border-gray-300 flex-shrink-0" style={{ height: '80px' }}>
              {header && (
                <h1 className={`${fontClasses.header} text-center`}>
                  {header}
                </h1>
              )}
              {subheader && aspectRatio !== '16:9' && (
                <p className={`${fontClasses.subheader} text-center mt-1`}>
                  {subheader}
                </p>
              )}
            </div>
          )}

          {/* Body Section - Takes all available space */}
          <div className="flex-1 min-h-0">
            {renderCardContent()}
          </div>

          {/* Footer Section */}
          {showFooter && (
            <div className="flex-shrink-0">
              <AssetFooter
                companyName={companyName}
                viewMode={viewMode}
                aspectRatio={aspectRatio}
                companyLogo={companyLogo}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={canvasRef}
      className={`flex-1 relative overflow-auto ${className}`}
      style={{
        minWidth: width ? '100%' : '500px',
        minHeight: height ? '100%' : '500px'
      }}
    >
      <div
        className="absolute bg-white shadow-xl rounded-xl border-2 border-gray-300 overflow-hidden"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: `${assetSize.width}px`,
          height: `${assetSize.height}px`,
          display: 'grid',
          gridTemplateRows: hasHeaderContent ? '80px 1fr 75px' : '1fr 75px'
        }}
      >
        {/* Header Section - Conditionally rendered */}
        {hasHeaderContent && (
          <div className="bg-white flex flex-col items-center justify-center px-4 py-2 border-b border-gray-300">
            {header && (
              <h1 className={`${fontClasses.header} text-center`}>
                {header}
              </h1>
            )}
            {subheader && aspectRatio !== '16:9' && (
              <p className={`${fontClasses.subheader} text-center mt-1`}>
                {subheader}
              </p>
            )}
          </div>
        )}

        {/* Body Section - Adjusts size based on header visibility */}
        {renderCardContent()}

        {/* Footer Section */}
        {showFooter && (
          <AssetFooter
            companyName={companyName}
            viewMode={viewMode}
            aspectRatio={aspectRatio}
            companyLogo={companyLogo}
          />
        )}
      </div>
    </div>
  );
};